/**
 * The machine-readable half of an API failure (issue #3142, initiative #3145,
 * design `docs/spec/error-codes.md`).
 *
 * A failure carries two things: prose for a person to read, and a code for the
 * program to branch on. Before this module only the prose survived the trip to
 * the browser, so ~30 call sites recovered the code by matching the prose —
 * `message.includes("Access denied")`, equality against a constant hand-copied
 * from a backend one, `includes("401")` (which matches any message containing
 * those digits, in any position). None
 * of it is visible to the type system and none of it is asserted by a test, so
 * rewording a backend string silently degraded a login prompt to a generic
 * error dialog.
 *
 * This module is the single reader of the code, and is deliberately pure: it
 * imports nothing from the API layer, so `ApiError` can use it to *populate*
 * `code` while call sites use it to *read* one. `readErrorCode` is idempotent —
 * running it on an `ApiError` that `toApiError` already normalized returns the
 * same code it stored.
 *
 * **Prose is for display. A code is for control flow.**
 */

/**
 * Codes describing what happened to the *request*, as opposed to which business
 * outcome occurred. Emitted by `GraphQLExceptionHandler` (a resolver threw),
 * `GraphQLTransportError` (rejected before execution reached a resolver), or
 * derived here from the HTTP status on the REST path.
 *
 * Domain codes (`NOT_SPEAKER_ROLE`, `SLOT_UNAVAILABLE`, `ACCOUNT_NOT_ACTIVATED`,
 * ...) are a separate set, not listed here: they ride on a GraphQL mutation
 * result payload (#3131) or on a REST error body's `errorCode` (#3174) rather
 * than on the transport. {@link readErrorCode} returns them unchanged;
 * {@link errorCode} narrows them away, so compare against a domain code
 * directly or use `readErrorCode`.
 */
const TRANSPORT_ERROR_CODES = [
  "UNAUTHENTICATED",
  "UNAUTHORIZED",
  "CLUB_ASSOCIATION_REQUIRED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "PAYMENT_REQUIRED",
  "BAD_USER_INPUT",
  "BAD_REQUEST",
  "GRAPHQL_PARSE_FAILED",
  "MAINTENANCE",
  "VIEW_AS_READ_ONLY",
  "INTERNAL_SERVER_ERROR",
] as const;

export type TransportErrorCode = (typeof TRANSPORT_ERROR_CODES)[number];

const TRANSPORT_ERROR_CODE_SET: ReadonlySet<string> = new Set(TRANSPORT_ERROR_CODES);

export function isTransportErrorCode(value: unknown): value is TransportErrorCode {
  return typeof value === "string" && TRANSPORT_ERROR_CODE_SET.has(value);
}

/**
 * The code implied by an HTTP status, for a failure that carried none of its
 * own. Only statuses the backend uses deliberately are mapped; anything else
 * (including every 5xx but 503) is left undefined rather than guessed, so a
 * caller sees "no code" instead of a wrong one.
 *
 * 401 maps to `UNAUTHENTICATED` and 403 to `FORBIDDEN`, matching the codes
 * `GraphQLTransportError` puts on those same statuses.
 */
export function codeFromHttpStatus(status: number | undefined): TransportErrorCode | undefined {
  switch (status) {
    case 400:
      return "BAD_USER_INPUT";
    case 401:
      return "UNAUTHENTICATED";
    case 402:
      // Entitlement, not permission: the club's subscription does not cover
      // this. Distinct from FORBIDDEN so a caller can tell the two apart.
      return "PAYMENT_REQUIRED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 503:
      return "MAINTENANCE";
    default:
      return undefined;
  }
}

/**
 * Marks an object whose `code` property is a machine-readable error code that
 * something already normalized, as opposed to a `code` meaning something else
 * entirely — axios puts `ERR_CANCELED` / `ECONNABORTED` / `ERR_BAD_REQUEST` on
 * exactly that property name.
 *
 * `ApiError` sets this in its constructor; nothing else does, and a `Symbol`
 * cannot be set by accident, so the test is exact. It exists here rather than
 * being answered with `instanceof ApiError` only because this module must stay
 * free of imports from the API layer — `ApiError` imports *it*.
 *
 * The alternative, guessing from the value by testing the string against
 * {@link TRANSPORT_ERROR_CODES}, is what this replaces. It answered a different
 * question than the one being asked, and so kept quietly dropping any code the
 * vocabulary did not list — which is precisely the domain codes #3131 adds.
 */
export const NORMALIZED_ERROR: unique symbol = Symbol.for("toasthost.normalizedError");

/** One GraphQL error entry, kept structural so no Apollo type is imported. */
interface GraphQLErrorLike {
  extensions?: { code?: unknown } | null;
}

/**
 * The Apollo / axios / `ApiError` surface this module reads. Structural rather
 * than imported, matching `guestRegistrationError.ts` and
 * `formatApolloErrorForLog`: the shapes come from three libraries and a failure
 * arrives as whichever one threw.
 */
interface ErrorLike {
  /** Set by `ApiError` (see {@link NORMALIZED_ERROR}), and by an axios error meaning something else. */
  code?: unknown;
  /** Present, and true, only on an already-normalized error. */
  [NORMALIZED_ERROR]?: boolean;
  /** Present on a thrown `ApolloError`. */
  graphQLErrors?: readonly (GraphQLErrorLike | null)[] | null;
  /** Present on a result resolved under `errorPolicy: "all"`, and on `ApiError.originalError`. */
  errors?: readonly (GraphQLErrorLike | null)[] | null;
  /** Present on a thrown `ApolloError` whose failure was at the transport. */
  networkError?: { statusCode?: number; result?: unknown } | null;
  /** Present on an axios error: the parsed REST error body (see {@link restBodyCode}). */
  response?: { status?: unknown; data?: unknown } | null;
  /** Set by `ApiError`: the raw thrown value, which for `runGraphQL` is the errors array. */
  originalError?: unknown;
  /** Set by `ApiError`. */
  status?: unknown;
}

function firstExtensionCode(errors: readonly (GraphQLErrorLike | null)[] | null | undefined): string | undefined {
  if (!errors) return undefined;
  for (const entry of errors) {
    const code = entry?.extensions?.code;
    if (typeof code === "string" && code.length > 0) return code;
  }
  return undefined;
}

function numericStatus(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

/**
 * The `errorCode` a REST error body carried, if any (issue #3174).
 *
 * The canonical envelope is `{ success: false, error: "<prose>", errorCode?: "<code>" }`
 * (`ControllerUtil` on the backend). The code lives in its own field rather than in `error`
 * because `error` is the human-readable message — `getServerErrorMessage` returns it as such, so
 * a code written there is displayed to the user verbatim.
 *
 * Only meaningful for the endpoints whose outcome the HTTP status cannot express (see
 * `AuthErrorCodes`); everywhere else the field is absent and the status-derived code stands.
 */
function restBodyCode(candidate: ErrorLike): string | undefined {
  const data = candidate.response?.data;
  if (!data || typeof data !== "object") return undefined;
  const code = (data as { errorCode?: unknown }).errorCode;
  return typeof code === "string" && code.length > 0 ? code : undefined;
}

function errorList(value: unknown): readonly (GraphQLErrorLike | null)[] | undefined {
  return Array.isArray(value) ? (value as readonly (GraphQLErrorLike | null)[]) : undefined;
}

/**
 * The GraphQL error list a failure carries, wherever it is hiding: on a
 * resolved result (`errors`), on a thrown `ApolloError` (`graphQLErrors`), or
 * on an `ApiError`'s `originalError` — which is the raw `errors` array when the
 * failure came through `runGraphQL`, and the raw `ApolloError` when it came
 * through `toApiError`.
 *
 * Finding the whole list, not just a code, is what {@link isOnlyAuthFailure}
 * needs: a normalized error stores only the first entry's code on `code`, so a
 * partial failure carrying an auth denial beside a genuine fault is
 * indistinguishable from a pure denial unless the list itself is recovered.
 *
 * One level deep, deliberately: `originalError` is a raw thrown value, never
 * another `ApiError` (`toApiError` returns an existing one unchanged rather
 * than re-wrapping), so there is no chain to walk and no cycle to guard.
 */
function graphQLErrorsOf(candidate: ErrorLike): readonly (GraphQLErrorLike | null)[] | undefined {
  if (candidate.graphQLErrors?.length) return candidate.graphQLErrors;
  if (candidate.errors?.length) return candidate.errors;

  const original = candidate.originalError;
  const direct = errorList(original);
  if (direct?.length) return direct;
  if (original && typeof original === "object") {
    const nested = original as ErrorLike;
    if (nested.graphQLErrors?.length) return nested.graphQLErrors;
    if (nested.errors?.length) return nested.errors;
  }

  return undefined;
}

/**
 * Pull the machine-readable code out of any thrown value or resolved result.
 *
 * Sources are tried in order of authority — an explicitly transmitted code
 * always beats one derived from a status:
 *
 * 1. an already-normalized `code` (`ApiError`), identified by the
 *    {@link NORMALIZED_ERROR} brand rather than by inspecting the string, so
 *    that axios's unrelated `code` on the same property is never mistaken for
 *    one and a code outside the vocabulary is never dropped;
 * 2. `extensions.code` on a thrown `ApolloError`'s `graphQLErrors`;
 * 3. `extensions.code` on a result's `errors` — the ordinary case, since the
 *    shared client sets `errorPolicy: "all"` so resolver errors resolve onto
 *    the result rather than rejecting (`graphql/client.ts`);
 * 4. the `GraphQLTransportError` envelope on a network-level rejection;
 * 5. `errorCode` on a REST error body (#3174), for the handful of endpoints whose
 *    outcome the status cannot express;
 * 6. the HTTP status, from Apollo's `networkError`, from `ApiError.status`, or
 *    from an axios `response`.
 *
 * Step 5 sits above step 6 for the reason the whole order exists: a code that was
 * transmitted always beats one inferred. A 400 carrying `INVALID_CHALLENGE_ANSWER`
 * would otherwise be indistinguishable from a 400 for a rejected password.
 *
 * Every step returns the string as sent, whether or not it is in the
 * vocabulary, so a code the backend adds is carried rather than dropped. Use
 * {@link errorCode} where a narrowed value is wanted.
 */
export function readErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const candidate = error as ErrorLike;

  // An already-normalized error's `code` is authoritative whatever the string,
  // so a domain code (#3131) or one the backend adds survives a round trip. The
  // brand is what makes reading it safe: axios's unrelated `code` lives on the
  // same property.
  if (candidate[NORMALIZED_ERROR] === true && typeof candidate.code === "string" && candidate.code.length > 0) {
    return candidate.code;
  }

  const fromGraphQL = firstExtensionCode(graphQLErrorsOf(candidate));
  if (fromGraphQL) return fromGraphQL;

  const envelope = candidate.networkError?.result;
  if (envelope && typeof envelope === "object") {
    const envelopeCode = (envelope as { code?: unknown }).code;
    if (typeof envelopeCode === "string" && envelopeCode.length > 0) return envelopeCode;
  }

  const fromRestBody = restBodyCode(candidate);
  if (fromRestBody) return fromRestBody;

  return codeFromHttpStatus(
    numericStatus(candidate.networkError?.statusCode) ??
      numericStatus(candidate.status) ??
      numericStatus(candidate.response?.status),
  );
}

/** {@link readErrorCode}, narrowed to the known vocabulary. */
export function errorCode(error: unknown): TransportErrorCode | undefined {
  const code = readErrorCode(error);
  return isTransportErrorCode(code) ? code : undefined;
}

/**
 * True when the failure means "this user must sign in before retrying".
 *
 * A predicate rather than a comparison because the question is not one-to-one
 * with a code: `GraphQLController` emits `UNAUTHENTICATED` for a rejected agent
 * or dev token, `GraphQLExceptionHandler` emits `UNAUTHORIZED` for a genuine
 * authentication failure, and REST answers 401 or 403. `CLUB_ASSOCIATION_REQUIRED`
 * and `FORBIDDEN` are deliberately excluded because the caller is already
 * authenticated and asking them to sign in again cannot create a club
 * association or grant a missing permission. Use {@link isForbidden} for the
 * latter.
 * When each call site decided this for itself they disagreed — the four
 * `isExpectedAuthError` copies this replaces had three different answers
 * between them.
 */
export function isAuthFailure(error: unknown): boolean {
  const code = errorCode(error);
  return code === "UNAUTHENTICATED" || code === "UNAUTHORIZED";
}

const AUTH_CODES: ReadonlySet<string> = new Set([
  "UNAUTHENTICATED",
  "UNAUTHORIZED",
  "CLUB_ASSOCIATION_REQUIRED",
  "FORBIDDEN",
]);

/**
 * True when a GraphQL error list is *entirely* auth denials.
 *
 * A different question from {@link isAuthFailure}, and both are needed. Under
 * `errorPolicy: "all"` a partial result can carry an auth denial on one field
 * beside a genuine fault on another, and a caller deciding "is this benign
 * enough to log at debug?" must not be satisfied by the first entry — that
 * downgrades the real fault out of sight. `isAuthFailure` asks about one
 * failure; this asks about a set.
 *
 * An empty or absent list is not an auth denial.
 */
export function everyErrorIsAuthFailure(errors: readonly (GraphQLErrorLike | null)[] | null | undefined): boolean {
  if (!errors || errors.length === 0) return false;
  return errors.every((entry) => {
    const code = entry?.extensions?.code;
    return typeof code === "string" && AUTH_CODES.has(code);
  });
}

/**
 * True when the failure is auth *and nothing else* — the question a caller asks
 * before downgrading a log line, as distinct from {@link isAuthFailure}, which
 * asks whether the user needs to sign in and is the right test before showing
 * them a login prompt.
 *
 * The distinction only bites on a multi-error GraphQL failure, where an auth
 * denial on one field can sit beside a genuine fault on another. Reporting that
 * pair as "expected auth state" logs the fault at debug and it is never seen,
 * which is why the four `isExpectedAuthError` copies this replaces all used
 * `every` rather than reading the first code.
 */
export function isOnlyAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const errors = graphQLErrorsOf(error as ErrorLike);
  return errors ? everyErrorIsAuthFailure(errors) : AUTH_CODES.has(errorCode(error) ?? "");
}

/**
 * True when the user is authenticated but lacks the permission, as distinct
 * from needing to sign in. Use this where showing a login prompt would be
 * wrong — a signed-in member hitting an officer-only resolver gets nothing
 * from being asked to log in again.
 */
export function isForbidden(error: unknown): boolean {
  return errorCode(error) === "FORBIDDEN";
}

export function isNotFound(error: unknown): boolean {
  return errorCode(error) === "NOT_FOUND";
}

export function isConflict(error: unknown): boolean {
  return errorCode(error) === "CONFLICT";
}

export function isBadInput(error: unknown): boolean {
  const code = errorCode(error);
  return code === "BAD_USER_INPUT" || code === "BAD_REQUEST";
}
